// components/GlobalRoleManager.tsx
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { User, Edit2, Save, X } from 'lucide-react';
import { updateUserGlobalRole } from '../store/slices/authSlice';
import { API_BASE_URL } from '../config/api';

const API_BASE = API_BASE_URL;

interface GlobalRoleManagerProps {
  userId: string;
  currentGlobalRole?: string | null;
  onUpdate?: () => void;
}

const GlobalRoleManager: React.FC<GlobalRoleManagerProps> = ({ 
  userId, 
  currentGlobalRole: initialGlobalRole,
  onUpdate 
}) => {
  const dispatch = useDispatch();
  const [globalRole, setGlobalRole] = useState<string>(initialGlobalRole || '');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestedRoles] = useState<string[]>([
    'Chercheur',
    'Consultant',
    'Expert Technique',
    'Analyste',
    'Coordinateur',
    'Partenaire',
    'Décideur',
    'Ministre',
    'Politique'
  ]);

  // Mettre à jour le state local quand currentGlobalRole change
  useEffect(() => {
    setGlobalRole(initialGlobalRole || '');
  }, [initialGlobalRole]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${API_BASE}/users/${userId}/global-role`,
        { globalRole: globalRole.trim() || null },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (response.data.success) {
        // Mettre à jour le store Redux avec le nouveau globalRole
        dispatch(updateUserGlobalRole(response.data.data.globalRole));
        setIsEditing(false);
        if (onUpdate) onUpdate();
      }
    } catch (err: any) {
      console.error('Erreur mise à jour rôle global:', err);
      alert(err.response?.data?.error || 'Erreur lors de la mise à jour du rôle');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setGlobalRole(initialGlobalRole || '');
    setIsEditing(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <User className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Rôle Global</h3>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Edit2 className="h-4 w-4" />
            Modifier
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Votre rôle professionnel
            </label>
            <input
              type="text"
              value={globalRole}
              onChange={(e) => setGlobalRole(e.target.value)}
              placeholder="Ex: Chercheur, Consultant, Expert Technique..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              list="suggested-roles"
            />
            <datalist id="suggested-roles">
              {suggestedRoles.map((role) => (
                <option key={role} value={role} />
              ))}
            </datalist>
            <p className="mt-1 text-xs text-gray-500">
              Ce rôle est visible dans tous vos projets (informations uniquement)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="h-4 w-4" />
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button
              onClick={handleCancel}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              <X className="h-4 w-4" />
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <div>
          {globalRole ? (
            <div className="flex items-center gap-2">
              <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {globalRole}
              </span>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">
              Aucun rôle global défini. Cliquez sur "Modifier" pour définir votre rôle.
            </p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            Ce rôle est visible dans tous vos projets et peut être modifié à tout moment.
          </p>
        </div>
      )}
    </div>
  );
};

export default GlobalRoleManager;
